import React, { createContext, useContext, useState, type ReactNode } from 'react';

export interface ChatContext {
  type: string;
  id: string;
  name?: string; // Optional: entity name for display in context chip
  /**
   * Read at SEND time, never at attach time: a spreadsheet's sheet and
   * on-screen selection can change between when the page attaches the
   * context and when the researcher hits send. Returns coordinates only,
   * never cell values (rule: never send or log cell contents).
   */
  readonly detail?: () => { sheet?: string; selection?: string };
  /**
   * Subscribe to a change in `detail()`'s value (a spreadsheet's selection or
   * active sheet). Returns an unsubscribe. Only a page that can detect the
   * change wires this up (the spreadsheet grid, via `GridHandle.onViewChange`);
   * a live session reads it to know when to resend `view_context`.
   */
  readonly onDetailChange?: (cb: () => void) => () => void;
}

interface ChatContextState {
  context: ChatContext | null;
  setContext: (context: ChatContext | null) => void;
}

const ChatContextContext = createContext<ChatContextState | undefined>(undefined);

export const useChatContextState = () => {
  const context = useContext(ChatContextContext);
  if (context === undefined) {
    throw new Error('useChatContextState must be used within a ChatContextProvider');
  }
  return context;
};

/**
 * The current chat context, or `null` when there is none OR no
 * `ChatContextProvider` is mounted above the caller.
 *
 * For a reader that must tolerate running outside the provider: a hook
 * unit-tested in isolation from the App-level provider (`useLiveViewContext`),
 * rather than one that owns setting the context, which keeps
 * `useChatContextState`'s hard "must be wrapped" contract for every existing
 * caller.
 */
export const useOptionalChatContext = (): ChatContext | null => {
  const state = useContext(ChatContextContext);
  return state?.context ?? null;
};

interface ChatContextProviderProps {
  children: ReactNode;
}

export const ChatContextProvider: React.FC<ChatContextProviderProps> = ({ children }) => {
  const [context, setContext] = useState<ChatContext | null>(null);

  return (
    <ChatContextContext.Provider value={{ context, setContext }}>
      {children}
    </ChatContextContext.Provider>
  );
};
